import { type, type Type } from 'arktype'

import type { Countries } from '@orus.eu/countries'
import { TechnicalError } from '@orus.eu/error'
import type { Language } from '@orus.eu/translations'

export const operatingZones = ['fr', 'es', 'nl'] as const

export function isOperatingZone(value: string): value is OperatingZone {
  const operatingZonesStrings: readonly string[] = operatingZones
  return operatingZonesStrings.includes(value)
}

export function assertIsOperatingZone(value: string): asserts value is OperatingZone {
  if (!isOperatingZone(value)) {
    throw new TechnicalError('Invalid operatingZone', { context: { value } })
  }
}

export const operatingZoneSchema: Type<(typeof operatingZones)[number]> = type('===', ...operatingZones)

/**
 * Enum of the geographical zones in which Orus operates
 */
export type OperatingZone = typeof operatingZoneSchema.infer

export type AvailableLanguagesByOperatingZone = Record<
  OperatingZone,
  {
    primaryLanguage: Language
    secondaryLanguages: Language[]
  }
>

export const availableLanguagesByOperatingZone: AvailableLanguagesByOperatingZone = {
  fr: { primaryLanguage: 'fr', secondaryLanguages: [] },
  es: { primaryLanguage: 'es', secondaryLanguages: [] },
  nl: { primaryLanguage: 'nl', secondaryLanguages: ['en'] },
}

const operatingZonesByCountry: Partial<Record<Countries, OperatingZone>> = {
  FR: 'fr',
  ES: 'es',
  NL: 'nl',
}

/**
 * Returns the operating zone a country belongs to, or `undefined` for the countries we don't operate in.
 */
export function getOperatingZoneForCountry(country: Countries): OperatingZone | undefined {
  return operatingZonesByCountry[country]
}

export function resolveLanguage(operatingZone: OperatingZone, userLanguage: Language | undefined): Language {
  const { primaryLanguage, secondaryLanguages } = availableLanguagesByOperatingZone[operatingZone]
  if (userLanguage === undefined) return primaryLanguage
  return primaryLanguage === userLanguage || secondaryLanguages.includes(userLanguage) ? userLanguage : primaryLanguage
}

export type CurrencyPosition = 'prefix' | 'suffix'

export function getCurrencyPositionForOperatingZone(operatingZone: OperatingZone): CurrencyPosition {
  switch (operatingZone) {
    case 'nl':
      return 'prefix'
    case 'fr':
    case 'es':
      return 'suffix'
  }
}
